import React, { useEffect, useState } from 'react';
import { Button, DatePicker, TimePicker, Input, List, Tag, Popconfirm, App } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api/client';

// Lightweight availability manager for tutors — reuses the /api/schedules endpoints.
// Slots feed the tutor's public profile so students can see when they're free.
export default function AvailabilityEditor({ userId }) {
  const { message } = App.useApp();
  const [slots, setSlots]   = useState([]);
  const [adding, setAdding] = useState(false);
  const [date, setDate]     = useState(null);
  const [range, setRange]   = useState(null);
  const [label, setLabel]   = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/api/users/${userId}/schedules`); setSlots(r.data); } catch {}
  };
  useEffect(() => { if (userId) load(); }, [userId]);

  const upcoming = slots
    .filter(s => dayjs(s.end_time).isAfter(dayjs().startOf('day')))
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const add = async () => {
    if (!date || !range) { message.warning('Pick a date and time range'); return; }
    setSaving(true);
    try {
      const start = date.hour(range[0].hour()).minute(0).second(0);
      const end   = date.hour(range[1].hour()).minute(0).second(0);
      await api.post('/api/schedules', {
        user_id:    userId,
        title:      label.trim() || 'Available',
        start_time: start.toISOString(),
        end_time:   end.toISOString(),
      });
      message.success('Availability added');
      setDate(null); setRange(null); setLabel(''); setAdding(false);
      load();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/api/schedules/${id}`); load(); }
    catch { message.error('Failed to remove'); }
  };

  return (
    <div style={{ border: '1px solid #e8e8e8', borderRadius: 12, background: '#fff', padding: '20px 24px', marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Availability</div>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Free slots students can see on your profile.</div>
        </div>
        {!adding && (
          <Button size="small" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Add slot</Button>
        )}
      </div>

      {adding && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16,
          background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}>
          <DatePicker value={date} onChange={setDate} format="D MMM YYYY"
            disabledDate={d => d && d < dayjs().startOf('day')} placeholder="Date" />
          <TimePicker.RangePicker value={range} onChange={setRange} format="h A" minuteStep={60} placeholder={['From', 'To']} />
          <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (optional)" style={{ width: 160 }} />
          <Button type="primary" size="small" loading={saving} onClick={add} style={{ background: '#0a0a0a', border: 'none' }}>Save</Button>
          <Button size="small" onClick={() => { setAdding(false); setDate(null); setRange(null); setLabel(''); }}>Cancel</Button>
        </div>
      )}

      {upcoming.length === 0
        ? <p style={{ color: '#888', fontSize: 13, margin: 0 }}>No upcoming availability. Add a slot above.</p>
        : (
          <List
            dataSource={upcoming}
            renderItem={(s, idx) => (
              <List.Item style={{ padding: `${idx === 0 ? 0 : 10}px 0 10px`, borderBottom: '1px solid #f5f5f5' }}
                extra={
                  <Popconfirm title="Remove this slot?" onConfirm={() => del(s.schedule_id)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                }>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>
                    {dayjs(s.start_time).format('ddd, D MMM')}
                    <span style={{ color: '#888', fontWeight: 400, marginLeft: 8 }}>
                      {dayjs(s.start_time).format('h A')} – {dayjs(s.end_time).format('h A')}
                    </span>
                  </div>
                  {s.title && s.title !== 'Available' && <Tag style={{ fontSize: 11, marginTop: 4 }}>{s.title}</Tag>}
                </div>
              </List.Item>
            )}
          />
        )
      }
    </div>
  );
}
